import { courseGenerationService } from '@/services/course-generation.service';
import TranslationProgressSection from '@/components/courses/TranslationProgressSection';
import {
  CourseOrchestration,
} from '@/types/orchestration.types';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  Circle,
  Clock,
  Database,
  Eye,
  FileText,
  Film,
  Loader2,
  Play,
  Sparkles,
  XCircle,
  Zap,
  RefreshCw,
  AlertTriangle,
  X,
  ThumbsUp,
  MessageSquareText,
  Send,
  Target,
  ClipboardCheck,
  Volume2,
  Video,
  Code2,
  GraduationCap,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import {
  COURSE_GENERATION_PERMISSION,
  COURSE_CATALOG_PERMISSION,
} from '@/constants/constant-permissions';

interface GenerationStage {
  key: keyof CourseOrchestration;
  label: string;
  description: string;
  icon: React.ComponentType<any>;
  estimatedTime: string;
  color: string;
}

// The Validation stage maps to the real `course_validation_status` field on the
// orchestration (start_time stamped after content expansion completes; flips to
// COMPLETED together with orchestration_status). If that field isn't populated
// yet (legacy orchestrations / the brief gap before the backend stamps it) the
// status is derived from content expansion + orchestration_status instead.
const VALIDATION_STAGE_KEY =
  'course_validation_status' as keyof CourseOrchestration;

export default function BuildCourse() {
  // Permission guard: either read OR write grants page access
  usePermissionRouteAccess([
    COURSE_GENERATION_PERMISSION.COURSE_GENERATION_READ,
    COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE,
  ]);
  // Action control: only catalog write permission enables Edit & Publish Course
  const canEditPublish = useHasPermission([
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE,
  ]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orchestrationId = searchParams.get('orchestrationId');

  const [isGenerating, setIsGenerating] = useState(true);
  const [generationStatus, setGenerationStatus] =
    useState<CourseOrchestration | null>(null);
  const [, forceUpdate] = useState(0);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const userExpandedStages = useRef<Set<string>>(new Set()); // Track user manual interactions

  const [isStuck, setIsStuck] = useState(false);

  // Skeleton approval state
  const [isApprovingSkeleton, setIsApprovingSkeleton] = useState(false);
  const [isRegeneratingSkeleton, setIsRegeneratingSkeleton] = useState(false);
  const [showSkeletonFeedbackModal, setShowSkeletonFeedbackModal] =
    useState(false);
  const [skeletonFeedback, setSkeletonFeedback] = useState('');

  // Full course regeneration state
  const [isRegeneratingFullCourse, setIsRegeneratingFullCourse] =
    useState(false);
  const [showRegenerateConfirmModal, setShowRegenerateConfirmModal] =
    useState(false);

  // Per-stage retry state
  const [retryingStage, setRetryingStage] = useState<string | null>(null);

  // Granular regeneration state
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set(),
  );
  const [expandedContentItems, setExpandedContentItems] = useState<Set<string>>(
    new Set(),
  );

  // New state for UI
  const [skeletonApprovalData, setSkeletonApprovalData] = useState<any>(null);

  // Scroll the skeleton approval section into view once it becomes actionable
  const skeletonApprovalRef = useRef<HTMLDivElement>(null);
  const hasScrolledToSkeletonApproval = useRef(false);

  // Image gallery state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedImageTitle, setSelectedImageTitle] = useState<string>('');

  // Track completion for one-time actions
  const [wasCompleted, setWasCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  // Generation stages configuration
  const stages: GenerationStage[] = [
    {
      key: 'metadata_generation_status' as keyof CourseOrchestration,
      label: 'Metadata Generation',
      description: 'Analyzing and generating course metadata',
      icon: Database,
      estimatedTime: '1-2 min',
      color: 'from-purple-500 to-indigo-600',
    },
    {
      key: 'image_generation_status' as keyof CourseOrchestration,
      label: 'Image Generation',
      description: 'Generating images for course content',
      icon: Film,
      estimatedTime: '3-5 min',
      color: 'from-pink-500 to-rose-600',
    },
    {
      key: 'course_skeleton_generation_status' as keyof CourseOrchestration,
      label: 'Course Skeleton',
      description: 'Creating course structure for approval',
      icon: BookOpen,
      estimatedTime: '2-4 min',
      color: 'from-green-500 to-emerald-600',
    },
    {
      key: 'module_content_expansion_status' as keyof CourseOrchestration,
      label: 'Content Expansion',
      description: 'Expanding modules with detailed content',
      icon: FileText,
      estimatedTime: '5-8 min',
      color: 'from-blue-500 to-cyan-600',
    },
  ];

  // Show the Content Validation stage as the 5th step whenever the course is
  // ACTIVELY GENERATING (so all 5 stages render and progress pending → active →
  // green as the run advances), OR for completed/failed courses where validation
  // actually ran (PROCESSING/COMPLETED/FAILED). This keeps it HIDDEN for legacy
  // completed courses, where the orchestration is COMPLETED but validation is
  // still at the Mongoose-hydrated NOT_STARTED default (the field is back-filled
  // on read even for courses that never had it in the DB).
  const orchStatus = generationStatus?.orchestration_status?.toUpperCase();
  const validationStatus =
    generationStatus?.course_validation_status?.status?.toUpperCase();
  const isStillGenerating =
    !!orchStatus && orchStatus !== 'COMPLETED' && orchStatus !== 'FAILED';
  const validationStarted =
    validationStatus === 'PROCESSING' ||
    validationStatus === 'COMPLETED' ||
    validationStatus === 'FAILED';
  if (isStillGenerating || validationStarted) {
    stages.push({
      key: VALIDATION_STAGE_KEY,
      label: 'Content Validation',
      description: 'Validating and finalizing all generated content',
      icon: ClipboardCheck,
      estimatedTime: '2-5 min',
      color: 'from-teal-500 to-emerald-600',
    });
  }

  // Toggle stage expansion
  const toggleStageExpansion = (stageKey: string) => {
    // Track that user manually interacted with this stage
    userExpandedStages.current.add(stageKey);

    setExpandedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stageKey)) {
        newSet.delete(stageKey);
      } else {
        newSet.add(stageKey);
      }
      return newSet;
    });
  };

  // Check if stage should be auto-expanded (only when action is needed)
  const shouldAutoExpand = (stage: GenerationStage, stageData: any) => {
    const actualStageStatus = stageData?.status?.toLowerCase();
    const isCompleted = actualStageStatus === 'completed';
    const isWaitingApproval =
      stage.key === 'course_skeleton_generation_status' &&
      isCompleted &&
      generationStatus?.skeleton_approval?.status === 'pending_approval';
    const isActive =
      actualStageStatus === 'processing' || actualStageStatus === 'in_progress';
    const isFailed = actualStageStatus === 'failed';

    // Auto-expand only if action is needed or stage is active/failed
    // Completed stages should be collapsed by default unless they need approval
    return isWaitingApproval || isActive || isFailed;
  };

  // Helper function to check if course generation is complete
  const isCourseGenerationComplete = (status: CourseOrchestration) => {
    // Use orchestration_status for overall completion check
    return status.orchestration_status?.toLowerCase() === 'completed';
  };

  // Derive the status of the synthetic Validation stage from content expansion
  // + orchestration_status. Validation begins once content expansion finishes
  // and is complete only when the orchestration flips to COMPLETED (which the
  // backend gates on post-DB validation finishing).
  const getDerivedValidationStatus = (
    status: CourseOrchestration | null = generationStatus,
  ): string => {
    if (!status) return 'pending';
    const orch = status.orchestration_status?.toLowerCase();
    const expansion = (
      status.module_content_expansion_status as any
    )?.status?.toLowerCase();

    if (orch === 'completed') return 'completed';
    if (orch === 'failed') return 'failed';
    if (expansion === 'completed') return 'processing'; // validation running
    return 'pending';
  };

  // Returns the effective {status, start_time, end_time} for a stage. Real
  // stages read straight off the orchestration; the synthetic Validation stage
  // is derived (starts when expansion ends, ends when orchestration COMPLETED).
  const getEffectiveStageData = (stageKey: keyof CourseOrchestration): any => {
    if (stageKey === VALIDATION_STAGE_KEY) {
      if (!generationStatus) return undefined;
      // Prefer the REAL field — it carries accurate start_time/end_time stamped
      // by the backend (validation starts after content expansion, ends when the
      // orchestration completes).
      const real = generationStatus.course_validation_status as any;
      if (real?.status) return real;

      // Fallback: derive when the field isn't populated yet (legacy runs / the
      // brief window before the backend stamps it).
      const expansion =
        generationStatus.module_content_expansion_status as any;
      const status = getDerivedValidationStatus();
      return {
        status,
        start_time: expansion?.end_time,
        end_time: status === 'completed' ? generationStatus.updatedAt : undefined,
      };
    }
    return generationStatus?.[stageKey] as any;
  };

  // Load generation status
  const loadGenerationStatus = useCallback(async () => {
    if (!orchestrationId) return;

    try {
      const status =
        await courseGenerationService.getOrchestrationStatus(orchestrationId);
      setGenerationStatus(status);

      // Auto-expand stages that need action (only if not manually managed)
      setExpandedStages((prev) => {
        const newExpandedStages = new Set(prev);
        stages.forEach((stage) => {
          const stageData = status[stage.key] as any;

          // If user has manually interacted with this stage, preserve their choice
          if (userExpandedStages.current.has(stage.key)) {
            return; // Don't auto-manage stages that user has manually controlled
          }

          // Auto-expand if stage needs action and wasn't already expanded
          if (shouldAutoExpand(stage, stageData) && !prev.has(stage.key)) {
            newExpandedStages.add(stage.key);
          }

          // Auto-collapse if no longer needs action and was auto-expanded
          // Also auto-collapse completed stages unless user manually expanded them
          if (
            !shouldAutoExpand(stage, stageData) &&
            prev.has(stage.key) &&
            !userExpandedStages.current.has(stage.key)
          ) {
            newExpandedStages.delete(stage.key);
          }
        });
        return newExpandedStages;
      });

      // Check if still generating based on individual stage completion
      const isNowCompleted = isCourseGenerationComplete(status);
      const isStillGenerating = !isNowCompleted;
      setIsGenerating(isStillGenerating);

      // Check for course completion transition
      if (isNowCompleted && !wasCompleted) {
        setWasCompleted(true);
        // Show celebration animation
        setTimeout(() => {
          setShowCelebration(true);
        }, 1000);
      }

      // Check if skeleton is ready for approval
      const skeletonCompleted =
        status.course_skeleton_generation_status?.status?.toLowerCase() ===
        'completed';
      const isSkeletonPendingApproval =
        status.skeleton_approval?.status === 'pending_approval';

      if (
        skeletonCompleted &&
        isSkeletonPendingApproval &&
        !skeletonApprovalData
      ) {
        setSkeletonApprovalData(status.course_skeleton_data);
      }
    } catch (error) {
      console.error('Error loading generation status:', error);
      toast.error('Failed to load generation status');
    }
  }, [orchestrationId, skeletonApprovalData]);

  // Approve skeleton
  const handleApproveSkeleton = async () => {
    if (!orchestrationId) return;

    setIsApprovingSkeleton(true);

    try {
      await courseGenerationService.approveSkeleton(orchestrationId);
      toast.success(
        'Course skeleton approved! Proceeding with content expansion.',
      );
      setSkeletonApprovalData(null);

      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Skeleton approval error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to approve skeleton',
      );
    } finally {
      setIsApprovingSkeleton(false);
    }
  };

  // Regenerate skeleton with feedback
  const handleRegenerateSkeleton = async () => {
    if (!orchestrationId || !skeletonFeedback.trim()) {
      toast.error('Please provide feedback for regeneration');
      return;
    }

    setIsRegeneratingSkeleton(true);

    try {
      await courseGenerationService.regenerateSkeleton(
        orchestrationId,
        skeletonFeedback,
      );
      toast.success('Feedback submitted! Regenerating course skeleton.');
      setShowSkeletonFeedbackModal(false);
      setSkeletonFeedback('');

      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Skeleton regeneration error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to regenerate skeleton',
      );
    } finally {
      setIsRegeneratingSkeleton(false);
    }
  };

  // Check if should show regenerate full course button
  const shouldShowRegenerateFullCourseButton = () => {
    if (!generationStatus) return false;

    const isCompleted = isCourseGenerationComplete(generationStatus);
    if (isCompleted) return false;

    // Check if skeleton approval is pending - HIDE button during approval
    const isWaitingForSkeletonApproval =
      generationStatus.course_skeleton_generation_status?.status ===
        'completed' &&
      generationStatus.skeleton_approval?.status === 'pending_approval';

    if (isWaitingForSkeletonApproval) {
      return false; // User should approve/reject skeleton, not regenerate
    }

    // Check if any pre-expansion stage (1-3) has failed — show immediately
    const hasPreExpansionFailure =
      generationStatus.metadata_generation_status?.status?.toLowerCase() ===
        'failed' ||
      generationStatus.image_generation_status?.status?.toLowerCase() ===
        'failed' ||
      generationStatus.course_skeleton_generation_status?.status?.toLowerCase() ===
        'failed';

    if (hasPreExpansionFailure) return true;

    // For content expansion failure or overall orchestration failure
    const isOrchestrationFailed =
      generationStatus.orchestration_status === 'FAILED';
    const hasExpansionFailure =
      generationStatus.module_content_expansion_status?.status?.toLowerCase() ===
      'failed';

    if (isOrchestrationFailed || hasExpansionFailure) return true;

    // Also show if stuck — threshold depends on content delivery type
    // video_presentation: 8 hours (video rendering takes much longer)
    // html / html_with_audio: 1 hour
    if (
      generationStatus.orchestration_status === 'PROCESSING' &&
      generationStatus.updatedAt
    ) {
      const is_video =
        generationStatus.content_delivery_type === 'video_presentation' || generationStatus.content_delivery_type === 'avatar_video';
      const stuck_threshold_minutes = is_video ? 8 * 60 : 60;
      const lastUpdate = new Date(generationStatus.updatedAt);
      const minutesSinceUpdate =
        (Date.now() - lastUpdate.getTime()) / (1000 * 60);
      if (minutesSinceUpdate > stuck_threshold_minutes) return true;
    }

    return false;
  };

  // Handle full course regeneration - Show modal
  const handleRegenerateFullCourse = () => {
    setShowRegenerateConfirmModal(true);
  };

  // Confirm and execute regeneration
  const confirmRegenerateFullCourse = async () => {
    if (!orchestrationId) return;

    setShowRegenerateConfirmModal(false);
    setIsRegeneratingFullCourse(true);

    try {
      await courseGenerationService.regenerateFullCourse(
        orchestrationId,
        'User requested full course regeneration - stuck or failed',
      );

      toast.success(
        'Full course regeneration initiated! Starting from scratch...',
      );

      // Reset UI state
      setWasCompleted(false);
      setShowCelebration(false);

      // Reload status after delay
      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Full course regeneration error:', error);
      toast.error(
        error.response?.data?.message || 'Failed to regenerate course',
      );
    } finally {
      setIsRegeneratingFullCourse(false);
    }
  };

  // Map stage keys to retry API stage names
  const stageKeyToRetryStage: Record<string, string> = {
    metadata_generation_status: 'METADATA',
    image_generation_status: 'IMAGE_GENERATION',
    course_skeleton_generation_status: 'COURSE_SKELETON',
    module_content_expansion_status: 'MODULE_CONTENT',
  };

  // Retry a specific stage (resets that stage + all subsequent stages)
  const handleRetryStage = async (stageKey: string) => {
    if (!orchestrationId) return;

    const retryStage = stageKeyToRetryStage[stageKey];
    if (!retryStage) return;

    setRetryingStage(stageKey);

    try {
      await courseGenerationService.retryOrchestrationStage(orchestrationId, {
        stage: retryStage as any,
        force: true,
      });
      toast.success(
        `Retrying ${stages.find((s) => s.key === stageKey)?.label || retryStage}...`,
      );

      setTimeout(() => {
        loadGenerationStatus();
      }, 2000);
    } catch (error: any) {
      console.error('Stage retry error:', error);
      toast.error(error.response?.data?.message || `Failed to retry stage`);
    } finally {
      setRetryingStage(null);
    }
  };

  // Check if a stage can show the retry button
  const canRetryStage = (stageKey: string): boolean => {
    if (!generationStatus) return false;
    const stageData = generationStatus[
      stageKey as keyof CourseOrchestration
    ] as any;
    const status = stageData?.status?.toLowerCase();
    return status === 'failed';
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      next.has(moduleId) ? next.delete(moduleId) : next.add(moduleId);
      return next;
    });
  };

  const toggleContentItem = (ciId: string) => {
    setExpandedContentItems((prev) => {
      const next = new Set(prev);
      next.has(ciId) ? next.delete(ciId) : next.add(ciId);
      return next;
    });
  };

  // Main useEffect
  useEffect(() => {
    if (!orchestrationId) {
      toast.error('No orchestration ID found');
      navigate('/admin/course/add-course');
      return;
    }

    loadGenerationStatus();

    return () => {
      courseGenerationService.stopPollingStatus();
    };
  }, [orchestrationId, navigate, loadGenerationStatus]);

  // Check for stuck status periodically
  useEffect(() => {
    if (!generationStatus) return;

    const isStuckStatus =
      courseGenerationService.isOrchestrationStuck(generationStatus);
    setIsStuck(isStuckStatus);

    // Don't show stuck message if waiting for user approval
    const isWaitingForApproval =
      generationStatus.course_skeleton_generation_status?.status?.toLowerCase() ===
        'completed' &&
      generationStatus.skeleton_approval?.status === 'pending_approval';

    if (isStuckStatus && !isStuck && !isWaitingForApproval) {
      const is_video =
        generationStatus.content_delivery_type === 'video_presentation' || generationStatus.content_delivery_type === 'avatar_video';
      const threshold_label = is_video ? '8 hours' : '1 hour';
      toast(
        `Course generation appears stuck — no progress for over ${threshold_label}. You can regenerate the full course or retry the failed stage.`,
        {
          duration: 10000,
          icon: '⚠️',
        },
      );
    }
  }, [generationStatus, isStuck]);

  // Bring the skeleton approval section into view when it becomes ready.
  // Fires once per approval cycle, so a regenerate-with-feedback scrolls again.
  const isSkeletonAwaitingApproval =
    generationStatus?.course_skeleton_generation_status?.status?.toLowerCase() ===
      'completed' &&
    generationStatus?.skeleton_approval?.status === 'pending_approval';

  useEffect(() => {
    if (!isSkeletonAwaitingApproval) {
      hasScrolledToSkeletonApproval.current = false;
      return;
    }
    if (hasScrolledToSkeletonApproval.current) return;
    hasScrolledToSkeletonApproval.current = true;

    // Make sure the section is rendered even if the user collapsed the stage
    setExpandedStages((prev) => {
      if (prev.has('course_skeleton_generation_status')) return prev;
      const next = new Set(prev);
      next.add('course_skeleton_generation_status');
      return next;
    });

    const timer = setTimeout(() => {
      skeletonApprovalRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [isSkeletonAwaitingApproval]);

  // Timer effect for live updates
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isGenerating) {
      timer = setInterval(() => {
        forceUpdate((prev) => prev + 1);
        loadGenerationStatus();
      }, 5000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isGenerating, loadGenerationStatus]);

  const getStageStatus = (stageName: keyof CourseOrchestration) => {
    if (!generationStatus)
      return { status: 'pending', icon: Circle, color: 'text-gray-400' };

    const stageData = getEffectiveStageData(stageName);
    const status = stageData?.status?.toLowerCase() || 'pending';

    switch (status) {
      case 'completed':
        return { status, icon: CheckCircle, color: 'text-green-600' };
      case 'processing':
      case 'in_progress':
        return {
          status: 'processing',
          icon: Loader2,
          color: 'text-blue-600',
          animate: true,
        };
      case 'failed':
        return { status, icon: XCircle, color: 'text-red-600' };
      case 'not_started':
        return { status: 'pending', icon: Circle, color: 'text-gray-400' };
      default:
        return { status: 'pending', icon: Circle, color: 'text-gray-400' };
    }
  };

  const getStageTimeTaken = (stageName: keyof CourseOrchestration) => {
    if (!generationStatus) return null;

    const stageData = getEffectiveStageData(stageName);
    if (!stageData?.start_time) return null;

    const startTime = new Date(stageData.start_time);
    // Use end_time if completed, otherwise use current time for live updates
    const endTime = stageData.end_time
      ? new Date(stageData.end_time)
      : new Date();
    const diffMs = endTime.getTime() - startTime.getTime();

    // Ensure time is never negative - handle timing issues gracefully
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));

    // Format time based on duration
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    } else if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (minutes === 0 && seconds === 0) {
        return `${hours}h`;
      } else if (seconds === 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${hours}h ${minutes}m ${seconds}s`;
      }
    }
  };

  // Calculate overall time from metadata start to module end (or current time if not ended)
  const getOverallTimeTaken = () => {
    if (!generationStatus) return null;

    const metadataStage = generationStatus.metadata_generation_status as any;

    // Need metadata start time to calculate overall duration
    if (!metadataStage?.start_time) return null;

    const startTime = new Date(metadataStage.start_time);
    // Overall course time runs from metadata start until the WHOLE pipeline is
    // done — i.e. through content validation, which completes when the
    // orchestration flips to COMPLETED. So the validation phase is included in
    // the total. While still running (incl. validation), tick live to now;
    // once complete, freeze at the orchestration's last-updated time.
    const validationEnd = (generationStatus.course_validation_status as any)
      ?.end_time;
    const endTime = isCourseGenerationComplete(generationStatus)
      ? new Date(validationEnd || generationStatus.updatedAt)
      : new Date();
    const diffMs = endTime.getTime() - startTime.getTime();

    // Ensure time is never negative - handle timing issues gracefully
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));

    // Format time based on duration
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    } else if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (minutes === 0 && seconds === 0) {
        return `${hours}h`;
      } else if (seconds === 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${hours}h ${minutes}m ${seconds}s`;
      }
    }
  };

  const renderImagePreviewModal = () => {
    if (!showImagePreview) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="relative max-w-4xl max-h-full w-full">
          <button
            onClick={() => setShowImagePreview(false)}
            className="absolute -top-12 right-0 text-white hover:text-gray-300 z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="bg-white rounded-lg overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-slate-50">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedImageTitle}
              </h3>
            </div>

            <div className="p-4 max-h-[80vh] overflow-auto">
              <img
                src={selectedImage}
                alt={selectedImageTitle}
                className="w-full h-auto rounded-lg shadow-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-image.png'; // fallback image
                }}
              />
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowImagePreview(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackModal = () => {
    if (!showSkeletonFeedbackModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Provide Feedback
            </h3>
            <button
              onClick={() => setShowSkeletonFeedbackModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What would you like to change about the course skeleton?
            </label>
            <textarea
              value={skeletonFeedback}
              onChange={(e) => setSkeletonFeedback(e.target.value)}
              placeholder="Describe the changes you'd like to see..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowSkeletonFeedbackModal(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRegenerateSkeleton}
              disabled={isRegeneratingSkeleton || !skeletonFeedback.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {isRegeneratingSkeleton ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!orchestrationId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Missing Orchestration ID
          </h2>
          <p className="text-gray-600 mb-4">
            No orchestration ID provided in the URL.
          </p>
          <button
            onClick={() => navigate('/admin/courses')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to Courses
          </button>
        </div>
      </div>
    );
  }

  const renderTimelineItem = (stage: GenerationStage, index: number) => {
    const stageStatus = getStageStatus(stage.key);
    const IconComponent = stageStatus.icon;
    const isCompleted = stageStatus.status === 'completed';
    const stageData = getEffectiveStageData(stage.key);
    const actualStageStatus = stageData?.status?.toLowerCase();
    const isActive =
      actualStageStatus === 'processing' || actualStageStatus === 'in_progress';
    const isFailed = stageStatus.status === 'failed';
    const isPending =
      stageStatus.status === 'pending' &&
      !isActive &&
      !isCompleted &&
      !isFailed;
    const isWaitingApproval =
      stage.key === 'course_skeleton_generation_status' &&
      isCompleted &&
      generationStatus?.skeleton_approval?.status === 'pending_approval';
    const isLast = index === stages.length - 1;
    const isExpanded = expandedStages.has(stage.key);
    const hasExpandableContent =
      shouldAutoExpand(stage, stageData) || isCompleted || isFailed;
    const timeTaken = getStageTimeTaken(stage.key);

    return (
      <div key={stage.key} className="relative">
        {/* Timeline Line */}
        {!isLast && (
          <div
            className={`absolute left-6 top-16 w-0.5 h-20 ${
              isCompleted
                ? 'bg-gradient-to-b from-green-500 to-emerald-600'
                : 'bg-gray-300'
            }`}
          ></div>
        )}

        {/* Timeline Item */}
        <div className="flex gap-4">
          {/* Status Circle */}
          <div
            className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 relative ${
              isCompleted
                ? 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-500 shadow-lg'
                : isActive
                  ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-blue-500 shadow-lg animate-pulse'
                  : isFailed
                    ? 'bg-gradient-to-br from-red-100 to-rose-100 border-red-500 shadow-lg'
                    : 'bg-gradient-to-br from-gray-100 to-slate-100 border-gray-300'
            }`}
          >
            {isActive && (
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full blur opacity-30 animate-pulse" />
            )}
            {isCompleted && (
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full blur opacity-20" />
            )}
            <IconComponent
              className={`w-6 h-6 ${
                isCompleted
                  ? 'text-green-600'
                  : isActive
                    ? 'text-blue-600'
                    : isFailed
                      ? 'text-red-600'
                      : 'text-gray-400'
              } ${stageStatus.animate ? 'animate-spin' : ''}`}
            />
          </div>

          {/* Content */}
          <div className="flex-1 pb-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
              {/* Header */}
              <div
                className={`p-6 ${!isExpanded && !(isCompleted || isFailed || isPending) ? 'rounded-2xl' : 'border-b border-gray-100 rounded-t-2xl'} ${hasExpandableContent ? 'cursor-pointer hover:bg-opacity-80' : ''} ${
                  isWaitingApproval
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50'
                    : isCompleted
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                      : isActive
                        ? 'bg-gradient-to-r from-blue-50 to-cyan-50'
                        : isFailed
                          ? 'bg-gradient-to-r from-red-50 to-rose-50'
                          : 'bg-gradient-to-r from-gray-50 to-slate-50'
                }`}
                onClick={
                  hasExpandableContent
                    ? () => toggleStageExpansion(stage.key)
                    : undefined
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {stage.label}
                    </h3>
                    <p className="text-sm text-gray-600">{stage.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isWaitingApproval
                            ? 'bg-amber-100 text-amber-800'
                            : isCompleted
                              ? 'bg-green-100 text-green-800'
                              : isActive
                                ? 'bg-blue-100 text-blue-800'
                                : isFailed
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {isWaitingApproval
                          ? 'Awaiting Approval'
                          : isCompleted
                            ? 'Completed'
                            : isActive
                              ? 'In Progress'
                              : isFailed
                                ? 'Failed'
                                : 'Pending'}
                      </div>
                    </div>
                    {hasExpandableContent && (
                      <div className="text-gray-400 hover:text-gray-600 transition-colors">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5" />
                        ) : (
                          <ChevronRight className="w-5 h-5" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Default Status Display (Always Visible) */}
              {!isExpanded && (isCompleted || isFailed || isPending) && (
                <div
                  className={`px-6 py-4 border-t border-gray-100 rounded-b-2xl ${
                    isCompleted
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                      : isFailed
                        ? 'bg-gradient-to-r from-red-50 to-rose-50'
                        : 'bg-gradient-to-r from-gray-50 to-slate-50'
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 ${
                      isCompleted
                        ? 'text-green-800'
                        : isFailed
                          ? 'text-red-800'
                          : 'text-gray-600'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : isFailed ? (
                      <XCircle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">
                      {isCompleted
                        ? 'Stage completed successfully'
                        : isFailed
                          ? 'Stage failed'
                          : 'Waiting to start'}
                    </span>
                    {isFailed && canRetryStage(stage.key) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRetryStage(stage.key);
                        }}
                        disabled={retryingStage === stage.key}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-gradient-to-r from-red-600 to-orange-600 rounded-md hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all ml-2"
                      >
                        {retryingStage === stage.key ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                        {retryingStage === stage.key ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                    {timeTaken && (
                      <div
                        className={`flex items-center gap-1 text-xs ml-auto ${
                          isCompleted
                            ? 'text-green-600'
                            : isFailed
                              ? 'text-red-600'
                              : 'text-gray-500'
                        }`}
                      >
                        <Clock
                          className={`w-3 h-3 ${isActive ? 'animate-spin' : ''}`}
                        />
                        <span>{timeTaken}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Expandable Content */}
              {isExpanded && (
                <>
                  {/* Progress Bar */}
                  {isActive && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-b-2xl">
                      <div className="flex items-center justify-between text-sm text-blue-600 mb-2">
                        <span className="font-medium">Processing...</span>
                        {timeTaken && (
                          <div className="flex items-center gap-1 font-medium">
                            <Clock className="w-3 h-3 animate-spin" />
                            <span>{timeTaken}</span>
                          </div>
                        )}
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full animate-pulse relative"
                          style={{ width: '65%' }}
                        >
                          <div className="absolute inset-0 bg-white/20 animate-pulse" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Metadata Generation Details */}
                  {stage.key === 'metadata_generation_status' &&
                    (isCompleted ||
                      generationStatus?.course_name ||
                      generationStatus?.short_description ||
                      generationStatus?.course_objectives ||
                      generationStatus?.tags ||
                      generationStatus?.takeaways) && (
                      <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="space-y-4">
                          {/* Course Information */}
                          {generationStatus?.course_name && (
                            <div className="bg-white rounded-xl p-4 border border-blue-200/50 shadow-sm">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-blue-900">
                                  Generated Course Information
                                </h4>
                                {timeTaken && (
                                  <div className="flex items-center gap-1 text-xs text-blue-600">
                                    <Clock
                                      className={`w-3 h-3 ${stage.key === 'metadata_generation_status' && isActive ? 'animate-spin' : ''}`}
                                    />
                                    <span>{timeTaken}</span>
                                  </div>
                                )}
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <span className="text-xs text-blue-700 block mb-1">
                                    Course Name
                                  </span>
                                  <p className="text-sm font-medium text-blue-900">
                                    {generationStatus.course_name}
                                  </p>
                                </div>
                                {generationStatus.category_name && (
                                  <div>
                                    <span className="text-xs text-blue-700 block mb-1">
                                      Category
                                    </span>
                                    <p className="text-sm font-medium text-blue-900">
                                      {generationStatus.category_name}
                                    </p>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <span className="text-xs text-blue-700 block mb-1">
                                      Difficulty
                                    </span>
                                    <p className="text-sm font-medium text-blue-900 capitalize">
                                      {generationStatus.difficulty_level?.toLowerCase()}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-blue-700 block mb-1">
                                      Tone
                                    </span>
                                    <p className="text-sm font-medium text-blue-900 capitalize">
                                      {generationStatus.tone?.toLowerCase()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Course Description */}
                          {generationStatus?.short_description && (
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                Short Description
                              </h4>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                {generationStatus.short_description}
                              </p>
                            </div>
                          )}

                          {/* Course Objectives */}
                          {generationStatus?.course_objectives && (
                            <div className="bg-white rounded-lg p-4 border border-green-200">
                              <h4 className="text-sm font-semibold text-green-900 mb-2">
                                Learning Objectives
                              </h4>
                              <div className="text-sm text-green-800 whitespace-pre-line">
                                {generationStatus.course_objectives}
                              </div>
                            </div>
                          )}

                          {/* Tags */}
                          {generationStatus?.tags &&
                            generationStatus.tags.length > 0 && (
                              <div className="bg-white rounded-lg p-4 border border-cyan-200">
                                <h4 className="text-sm font-semibold text-cyan-900 mb-2">
                                  Course Tags
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {generationStatus.tags
                                    .slice(0, 8)
                                    .map((tag, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-cyan-100 text-cyan-800"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  {generationStatus.tags.length > 8 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-cyan-200 text-cyan-900">
                                      +{generationStatus.tags.length - 8} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Key Takeaways */}
                          {generationStatus?.takeaways &&
                            generationStatus.takeaways.length > 0 && (
                              <div className="bg-white rounded-lg p-4 border border-amber-200">
                                <h4 className="text-sm font-semibold text-amber-900 mb-2">
                                  Key Learning Takeaways
                                </h4>
                                <ul className="space-y-1">
                                  {generationStatus.takeaways
                                    .slice(0, 3)
                                    .map((takeaway, index) => (
                                      <li
                                        key={index}
                                        className="text-sm text-amber-800 flex items-start gap-2"
                                      >
                                        <span className="text-amber-600 mt-1">
                                          •
                                        </span>
                                        <span>{takeaway}</span>
                                      </li>
                                    ))}
                                  {generationStatus.takeaways.length > 3 && (
                                    <li className="text-sm text-amber-700 font-medium">
                                      +{generationStatus.takeaways.length - 3}{' '}
                                      more takeaways
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                      </div>
                    )}

                  {/* Generated Images Gallery */}
                  {stage.key === 'image_generation_status' && isCompleted && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50 rounded-b-2xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Eye className="w-5 h-5 text-purple-600" />
                          <h4 className="font-medium text-purple-900">
                            Generated Images
                          </h4>
                        </div>
                        {timeTaken && (
                          <div className="flex items-center gap-1 text-xs text-purple-600">
                            <Clock
                              className={`w-3 h-3 ${stage.key === 'image_generation_status' && isActive ? 'animate-spin' : ''}`}
                            />
                            <span>{timeTaken}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-purple-800 mb-4">
                        Course images have been generated successfully. Click on
                        any image to preview it.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Banner Image */}
                        {generationStatus?.banner_image_url && (
                          <div
                            className="relative group cursor-pointer"
                            onClick={() => {
                              setSelectedImage(
                                generationStatus.banner_image_url || '',
                              );
                              setSelectedImageTitle('Course Banner Image');
                              setShowImagePreview(true);
                            }}
                          >
                            <div className="aspect-[16/9] bg-white rounded-lg overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-colors">
                              <img
                                src={generationStatus.banner_image_url}
                                alt="Course Banner"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                            <p className="text-sm text-center mt-2 text-gray-700 font-medium">
                              Banner Image
                            </p>
                          </div>
                        )}

                        {/* Thumbnail Image */}
                        {generationStatus?.thumbnail_image_url && (
                          <div
                            className="relative group cursor-pointer"
                            onClick={() => {
                              setSelectedImage(
                                generationStatus.thumbnail_image_url || '',
                              );
                              setSelectedImageTitle('Course Thumbnail Image');
                              setShowImagePreview(true);
                            }}
                          >
                            <div className="aspect-[16/9] bg-white rounded-lg overflow-hidden border-2 border-purple-200 hover:border-purple-400 transition-colors">
                              <img
                                src={generationStatus.thumbnail_image_url}
                                alt="Course Thumbnail"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                            <p className="text-sm text-center mt-2 text-gray-700 font-medium">
                              Thumbnail Image
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Course Skeleton Details - Only show when completed but NOT waiting for approval */}
                  {stage.key === 'course_skeleton_generation_status' &&
                    isCompleted &&
                    !isWaitingApproval &&
                    generationStatus?.course_skeleton_data
                      ?.module_structure && (
                      <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 rounded-b-2xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <h4 className="font-medium text-green-900">
                              Course Skeleton Generated
                            </h4>
                          </div>
                          {timeTaken && (
                            <div className="flex items-center gap-1 text-xs text-green-600">
                              <Clock className="w-3 h-3" />
                              <span>{timeTaken}</span>
                            </div>
                          )}
                        </div>

                        {/* Skeleton Preview */}
                        <div className="bg-white rounded-lg p-4 mb-4 border">
                          <h5 className="font-medium text-gray-900 mb-2">
                            Course Skeleton Overview
                          </h5>
                          <div className="space-y-4">
                            {/* Course Overview */}
                            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                              <h6 className="font-medium text-gray-900 mb-1">
                                Course Overview
                              </h6>
                              <p className="text-sm text-gray-600 mb-2">
                                {generationStatus.course_name}
                              </p>
                              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                                <span>
                                  Total Modules:{' '}
                                  {
                                    generationStatus.course_skeleton_data
                                      .module_structure.total_modules
                                  }
                                </span>
                                {/* <span>Duration: {Math.round(generationStatus.course_skeleton_data.module_structure.estimated_total_duration / 60)} hours</span> */}
                              </div>
                            </div>

                            {/* Modules List */}
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-900">
                                Modules (
                                {
                                  generationStatus.course_skeleton_data
                                    .module_structure.modules.length
                                }
                                )
                              </p>
                              {generationStatus.course_skeleton_data.module_structure.modules.map(
                                (module: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200"
                                  >
                                    <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center border border-blue-300">
                                      <span className="text-xs font-medium text-blue-600">
                                        {module.module_number}
                                      </span>
                                    </div>
                                    <div className="flex-1">
                                      <h6 className="font-medium text-gray-900 text-sm">
                                        {module.title}
                                      </h6>
                                      <p className="text-xs text-gray-600 mt-1">
                                        {module.description}
                                      </p>
                                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                        <span>📚 {module.pedagogy_level}</span>
                                        {/* <span>⏱️ {Math.round(module.estimated_duration / 60)} hours</span> */}
                                        <span>
                                          🎯{' '}
                                          {module.learning_objectives?.length ||
                                            0}{' '}
                                          objectives
                                        </span>
                                      </div>
                                      {/* Learning Objectives */}
                                      {module.learning_objectives &&
                                        module.learning_objectives.length >
                                          0 && (
                                          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-100">
                                            <span className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                              <Target className="w-3 h-3" />
                                              Learning Objectives
                                            </span>
                                            <ul className="space-y-1">
                                              {module.learning_objectives.map(
                                                (
                                                  objective: string,
                                                  objIdx: number,
                                                ) => (
                                                  <li
                                                    key={objIdx}
                                                    className="text-xs text-gray-600 flex items-start gap-2"
                                                  >
                                                    <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                                      •
                                                    </span>
                                                    <span className="leading-relaxed">
                                                      {objective}
                                                    </span>
                                                  </li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>

                            {/* Quality Indicators */}
                            {generationStatus.course_skeleton_data
                              .content_quality_indicators && (
                              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                                <h6 className="font-medium text-green-900 mb-2">
                                  Quality Assessment
                                </h6>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .progression_clarity * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Progression
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .objective_alignment * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Alignment
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .audience_appropriateness * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Audience Fit
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Skeleton Approval Section - Only show when waiting for approval */}
                  {isWaitingApproval && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50 rounded-b-2xl">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          <h4 className="font-medium text-amber-900">
                            Review Required
                          </h4>
                        </div>
                        {timeTaken && (
                          <div className="flex items-center gap-1 text-xs text-amber-600">
                            <Clock
                              className={`w-3 h-3 ${stage.key === 'course_skeleton_generation_status' && isActive ? 'animate-spin' : ''}`}
                            />
                            <span>{timeTaken}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-amber-800 mb-4">
                        The course skeleton has been generated. Please review
                        and approve to continue with content expansion.
                      </p>

                      {/* Skeleton Preview */}
                      <div className="bg-white rounded-lg p-4 mb-4 border">
                        <h5 className="font-medium text-gray-900 mb-2">
                          Course Skeleton Preview
                        </h5>
                        {generationStatus?.course_skeleton_data
                          ?.module_structure && (
                          <div className="space-y-4">
                            {/* Course Overview */}
                            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                              <h6 className="font-medium text-gray-900 mb-1">
                                Course Overview
                              </h6>
                              <p className="text-sm text-gray-600 mb-2">
                                {generationStatus.course_name}
                              </p>
                              <div className="text-xs text-gray-500">
                                <span>
                                  Total Modules:{' '}
                                  {
                                    generationStatus.course_skeleton_data
                                      .module_structure.total_modules
                                  }
                                </span>
                              </div>
                            </div>

                            {/* Modules List */}
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-900">
                                Modules (
                                {
                                  generationStatus.course_skeleton_data
                                    .module_structure.modules.length
                                }
                                )
                              </p>
                              {generationStatus.course_skeleton_data.module_structure.modules.map(
                                (module: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="flex items-start gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200"
                                  >
                                    <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center border border-blue-300">
                                      <span className="text-xs font-medium text-blue-600">
                                        {module.module_number}
                                      </span>
                                    </div>
                                    <div className="flex-1">
                                      <h6 className="font-medium text-gray-900 text-sm">
                                        {module.title}
                                      </h6>
                                      <p className="text-xs text-gray-600 mt-1">
                                        {module.description}
                                      </p>
                                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                        <span>📚 {module.pedagogy_level}</span>
                                        <span>
                                          🎯{' '}
                                          {module.learning_objectives?.length ||
                                            0}{' '}
                                          objectives
                                        </span>
                                      </div>
                                      {/* Learning Objectives */}
                                      {module.learning_objectives &&
                                        module.learning_objectives.length >
                                          0 && (
                                          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-100">
                                            <span className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                              <Target className="w-3 h-3" />
                                              Learning Objectives
                                            </span>
                                            <ul className="space-y-1">
                                              {module.learning_objectives.map(
                                                (
                                                  objective: string,
                                                  objIdx: number,
                                                ) => (
                                                  <li
                                                    key={objIdx}
                                                    className="text-xs text-gray-600 flex items-start gap-2"
                                                  >
                                                    <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                                      •
                                                    </span>
                                                    <span className="leading-relaxed">
                                                      {objective}
                                                    </span>
                                                  </li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>

                            {/* Quality Indicators */}
                            {generationStatus.course_skeleton_data
                              .content_quality_indicators && (
                              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                                <h6 className="font-medium text-green-900 mb-2">
                                  Quality Assessment
                                </h6>
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .progression_clarity * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Progression
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .objective_alignment * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Alignment
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-medium text-green-800">
                                      {(
                                        generationStatus.course_skeleton_data
                                          .content_quality_indicators
                                          .audience_appropriateness * 10
                                      ).toFixed(1)}
                                      /10
                                    </div>
                                    <div className="text-green-600">
                                      Audience Fit
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div ref={skeletonApprovalRef} className="flex gap-3">
                        <button
                          onClick={handleApproveSkeleton}
                          disabled={isApprovingSkeleton}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          {isApprovingSkeleton ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Approving...
                            </>
                          ) : (
                            <>
                              <ThumbsUp className="w-4 h-4" />
                              Approve & Continue
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setShowSkeletonFeedbackModal(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          <MessageSquareText className="w-4 h-4" />
                          Regenerate with Feedback
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Module Content Expansion Details — Hierarchical Tree */}
                  {stage.key === 'module_content_expansion_status' && (
                    <div
                      className={`p-6 border-t border-gray-100 ${isCompleted ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-cyan-50'} rounded-b-2xl`}
                    >
                      {/* Progress Overview */}
                      <div
                        className={`bg-white rounded-lg p-4 border ${isCompleted ? 'border-emerald-200' : 'border-blue-200'} mb-4`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4
                            className={`text-sm font-semibold ${isCompleted ? 'text-emerald-900' : 'text-blue-900'}`}
                          >
                            Content Expansion Progress
                          </h4>
                          {timeTaken && (
                            <div
                              className={`flex items-center gap-1 text-xs ${isCompleted ? 'text-emerald-600' : 'text-blue-600'}`}
                            >
                              <Clock
                                className={`w-3 h-3 ${isActive ? 'animate-spin' : ''}`}
                              />
                              <span>{timeTaken}</span>
                            </div>
                          )}
                        </div>
                        <div
                          className={`grid ${generationStatus?.content_delivery_type === 'video_presentation' || generationStatus?.content_delivery_type === 'avatar_video' ? 'grid-cols-3' : 'grid-cols-2'} gap-3 text-center`}
                        >
                          <div>
                            <p
                              className={`text-lg font-bold ${isCompleted ? 'text-emerald-700' : 'text-blue-700'}`}
                            >
                              {generationStatus?.course_skeleton
                                ?.completion_stats?.total_modules ||
                                generationStatus?.total_modules ||
                                0}
                            </p>
                            <span className="text-xs text-gray-500">
                              Modules
                            </span>
                          </div>
                          <div>
                            <p
                              className={`text-lg font-bold ${isCompleted ? 'text-emerald-700' : 'text-blue-700'}`}
                            >
                              {generationStatus?.course_skeleton
                                ?.completion_stats?.total_content_items ||
                                generationStatus?.total_content_items ||
                                0}
                            </p>
                            <span className="text-xs text-gray-500">
                              Content Items
                            </span>
                          </div>
                          {generationStatus?.content_delivery_type ===
                            'video_presentation' && (
                            <div>
                              <p
                                className={`text-lg font-bold ${isCompleted ? 'text-emerald-700' : 'text-blue-700'}`}
                              >
                                {generationStatus?.course_skeleton
                                  ?.completion_stats?.completed_scenes || 0}
                                
                              </p>
                              <span className="text-xs text-gray-500">
                                Scenes
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Overall progress bar */}
                        <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${isCompleted ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}
                            style={{
                              width: `${isCompleted ? 100 : generationStatus?.course_skeleton?.completion_stats?.completion_percentage || 0}%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Course Content Tree View */}
                      {generationStatus?.course_skeleton?.modules &&
                        generationStatus.course_skeleton.modules.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hidden">
                          {/* Hidden for future */}
                            <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900">
                                Course Structure
                              </h4>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {/* Course Pre-Assessment */}
                              {(generationStatus.course_skeleton as any)
                                .course_pre_assessment_count > 0 && (
                                <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50/50">
                                  <ClipboardCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                  <span className="text-sm font-medium text-amber-900">
                                    Course Pre-Assessment
                                  </span>
                                  <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                                    {
                                      (generationStatus.course_skeleton as any)
                                        .course_pre_assessment_count
                                    }{' '}
                                    questions
                                  </span>
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                </div>
                              )}

                              {/* Modules */}
                              {generationStatus.course_skeleton.modules.map(
                                (module: any, moduleIdx: number) => {
                                  const moduleId = module._id;
                                  const isModuleExpanded =
                                    expandedModules.has(moduleId);
                                  const isPresentation =
                                    generationStatus.content_delivery_type ===
                                    'video_presentation';
                                  const moduleComplete =
                                    module.completion_percentage === 100;
                                  const moduleProcessing = isPresentation
                                    ? module.total_scenes > 0 &&
                                      module.completed_scenes > 0 &&
                                      !moduleComplete
                                    : module.completion_percentage > 0 &&
                                      !moduleComplete;

                                  return (
                                    <div key={moduleId}>
                                      {/* Module Header */}
                                      <div
                                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${moduleComplete ? 'bg-green-50/30' : ''}`}
                                        onClick={() => toggleModule(moduleId)}
                                      >
                                        {isModuleExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                        )}
                                        <BookOpen className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                                        <span className="text-sm font-medium text-gray-900 truncate">
                                          Module {moduleIdx + 1}: {module.title}
                                        </span>
                                        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                                          {generationStatus.content_delivery_type ===
                                            'video_presentation' && (
                                            <span className="text-xs text-gray-500">
                                              {module.completed_scenes}/
                                              {module.total_scenes} scenes
                                            </span>
                                          )}
                                          {moduleComplete ? (
                                            <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                          ) : moduleProcessing ? (
                                            <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />
                                          ) : (
                                            <Circle className="w-3.5 h-3.5 text-gray-400" />
                                          )}
                                        </span>
                                      </div>

                                      {/* Module Expanded Content */}
                                      {isModuleExpanded && (
                                        <div className="bg-gray-50/50">
                                          {/* Module Pre-Assessment */}
                                          {module.pre_assessment_count > 0 && (
                                            <div className="flex items-center gap-3 px-4 py-2 pl-12">
                                              <ClipboardCheck className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                                              <span className="text-xs font-medium text-amber-800">
                                                Pre-Assessment
                                              </span>
                                              <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                                {module.pre_assessment_count}q
                                              </span>
                                              <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                            </div>
                                          )}

                                          {/* Content Items */}
                                          {(module.content_items || []).map(
                                            (ci: any, ciIdx: number) => {
                                              const ciId = ci._id;
                                              const isCiExpanded =
                                                expandedContentItems.has(ciId);
                                              const ciComplete = isPresentation
                                                ? ci.completed_scenes ===
                                                    ci.total_scenes &&
                                                  ci.total_scenes > 0
                                                : ci.generation_status ===
                                                  'completed';
                                              const ciProcessing =
                                                isPresentation
                                                  ? ci.total_scenes > 0 &&
                                                    ci.completed_scenes > 0 &&
                                                    !ciComplete
                                                  : ci.generation_status ===
                                                    'processing';

                                              return (
                                                <div key={ciId}>
                                                  {/* Content Item Header */}
                                                  <div
                                                    className={`flex items-center gap-3 px-4 py-2 pl-12 cursor-pointer hover:bg-gray-100/50 transition-colors`}
                                                    onClick={() =>
                                                      toggleContentItem(ciId)
                                                    }
                                                  >
                                                    {isCiExpanded ? (
                                                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                    ) : (
                                                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                    )}
                                                    <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                                    <span className="text-xs font-medium text-gray-800 truncate">
                                                      {ciIdx + 1}. {ci.title}
                                                    </span>
                                                    <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                                                      {generationStatus.content_delivery_type ===
                                                        'video_presentation' && (
                                                        <span className="text-xs text-gray-500">
                                                          {ci.completed_scenes}/
                                                          {ci.total_scenes}
                                                        </span>
                                                      )}
                                                      {ciComplete ? (
                                                        <CheckCircle className="w-3 h-3 text-green-600" />
                                                      ) : ciProcessing ? (
                                                        <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
                                                      ) : (
                                                        <Circle className="w-3 h-3 text-gray-400" />
                                                      )}
                                                    </span>
                                                  </div>

                                                  {/* Content Item Pre-Assessment */}
                                                  {isCiExpanded &&
                                                    ci.pre_assessment_count >
                                                      0 && (
                                                      <div className="flex items-center gap-3 px-4 py-1.5 pl-20">
                                                        <ClipboardCheck className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                                        <span className="text-xs text-amber-700">
                                                          Pre-Assessment
                                                        </span>
                                                        <span className="ml-auto text-xs text-amber-600">
                                                          {
                                                            ci.pre_assessment_count
                                                          }
                                                          q
                                                        </span>
                                                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                                      </div>
                                                    )}

                                                  {/* Scenes — only applicable for video_presentation delivery type */}
                                                  {generationStatus.content_delivery_type ===
                                                    'video_presentation' &&
                                                    isCiExpanded &&
                                                    [...(ci.scenes || [])]
                                                      .sort(
                                                        (a: any, b: any) =>
                                                          (a.scene_number ||
                                                            0) -
                                                          (b.scene_number || 0),
                                                      )
                                                      .map((scene: any) => (
                                                        <div
                                                          key={scene._id}
                                                          className="flex items-center gap-3 px-4 py-1.5 pl-20"
                                                        >
                                                          <Film className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                                          <span className="text-xs text-gray-700 truncate">
                                                            Scene{' '}
                                                            {scene.scene_number}
                                                            : {scene.title}
                                                          </span>
                                                          {/* Scene sub-statuses */}
                                                          <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                                                            <span
                                                              title="HTML uploaded"
                                                              className={`flex items-center gap-0.5 text-xs ${scene.has_html ? 'text-green-600' : 'text-gray-400'}`}
                                                            >
                                                              <Code2 className="w-3 h-3" />
                                                            </span>
                                                            <span
                                                              title="Audio generated"
                                                              className={`flex items-center gap-0.5 text-xs ${scene.has_audio ? 'text-green-600' : 'text-gray-400'}`}
                                                            >
                                                              <Volume2 className="w-3 h-3" />
                                                            </span>
                                                            <span
                                                              title="Video rendered"
                                                              className={`flex items-center gap-0.5 text-xs ${scene.has_video ? 'text-green-600' : 'text-gray-400'}`}
                                                            >
                                                              <Video className="w-3 h-3" />
                                                            </span>
                                                          </span>
                                                        </div>
                                                      ))}

                                                  {/* Content Item Post-Assessment */}
                                                  {isCiExpanded &&
                                                    ci.post_assessment_count >
                                                      0 && (
                                                      <div className="flex items-center gap-3 px-4 py-1.5 pl-20">
                                                        <ClipboardCheck className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                                        <span className="text-xs text-amber-700">
                                                          Post-Assessment
                                                        </span>
                                                        <span className="ml-auto text-xs text-amber-600">
                                                          {
                                                            ci.post_assessment_count
                                                          }
                                                          q
                                                        </span>
                                                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            },
                                          )}

                                          {/* Module Post-Assessment */}
                                          {module.post_assessment_count > 0 && (
                                            <div className="flex items-center gap-3 px-4 py-2 pl-12">
                                              <ClipboardCheck className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                                              <span className="text-xs font-medium text-amber-800">
                                                Post-Assessment
                                              </span>
                                              <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
                                                {module.post_assessment_count}q
                                              </span>
                                              <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                },
                              )}

                              {/* Course Post-Assessment */}
                              {(generationStatus.course_skeleton as any)
                                .course_post_assessment_count > 0 && (
                                <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50/50">
                                  <GraduationCap className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                  <span className="text-sm font-medium text-amber-900">
                                    Course Post-Assessment
                                  </span>
                                  <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                                    {
                                      (generationStatus.course_skeleton as any)
                                        .course_post_assessment_count
                                    }{' '}
                                    questions
                                  </span>
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Completion Status */}
                  {isCompleted &&
                    !isWaitingApproval &&
                    stage.key !== 'module_content_expansion_status' && (
                      <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 rounded-b-2xl">
                        <div className="flex items-center gap-2 text-green-800">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">
                            Stage completed successfully
                          </span>
                      </div>
                      {/* Hidden for future */}
                        {stage.key === 'course_skeleton_generation_status' &&
                          generationStatus?.skeleton_approval?.status ===
                            'approvedd' && (
                            <p className="text-xs text-green-700 mt-1 ml-7">
                              Approved by{' '}
                              {generationStatus.skeleton_approval
                                .approved_by_name ||
                                generationStatus.skeleton_approval
                                  .approved_by ||
                                'System'}
                              {generationStatus.skeleton_approval
                                .approved_at && (
                                <span>
                                  {' '}
                                  on{' '}
                                  {new Date(
                                    generationStatus.skeleton_approval
                                      .approved_at,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          )}
                      </div>
                    )}

                  {/* Error Status with Retry Button */}
                  {isFailed && (
                    <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-red-50 to-rose-50 rounded-b-2xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-red-800">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">Stage failed</span>
                          {stageData?.error_details && (
                            <span
                              className="text-xs text-red-600 ml-2 max-w-md truncate"
                              title={stageData.error_details}
                            >
                              — {stageData.error_details}
                            </span>
                          )}
                        </div>
                        {canRetryStage(stage.key) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRetryStage(stage.key);
                            }}
                            disabled={retryingStage === stage.key}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-orange-600 rounded-lg hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                          >
                            {retryingStage === stage.key ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-3.5 h-3.5" />
                                Retry This Stage
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-[calc(100vh-5.25rem)] bg-gray-50">
        <div className="mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin/course/add-course')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span>Back to Course Creation</span>
            </button>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                    Course Generation in Progress
                  </h1>
                  <p className="text-gray-600">
                    {isGenerating ? (
                      <span className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-500 animate-pulse" />
                        Your AI course is being crafted.
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Course generation completed successfully!
                      </span>
                    )}
                  </p>
                  {generationStatus?.created_by_user && (
                    <p className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                      <User className="w-4 h-4" />
                      <span>
                        Created by{' '}
                        <span className="font-medium text-gray-700">
                          {generationStatus.created_by_user.full_name || 'Unknown user'}
                        </span>
                        {generationStatus.created_by_user.email && (
                          <>
                            {' · '}
                            <span className="text-gray-500">
                              {generationStatus.created_by_user.email}
                            </span>
                          </>
                        )}
                      </span>
                    </p>
                  )}
                </div>
                {getOverallTimeTaken() && (
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
                      isGenerating
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-green-50 text-green-700 border-green-200'
                    }`}
                  >
                    <Clock
                      className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`}
                    />
                    <span>Total time: {getOverallTimeTaken()}</span>
                  </div>
                )}
              </div>

              {/* Course Details */}
              {generationStatus?.user_course_idea && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs text-gray-600 block mb-2">
                      User's Course Idea
                    </span>
                    <p className="text-sm font-medium text-gray-900 leading-relaxed">
                      {generationStatus.user_course_idea}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className="text-xs text-gray-600 block mb-1">
                        Language
                      </span>
                      <p className="text-sm font-medium text-gray-900">
                        {generationStatus.language_config?.language_code ||
                          generationStatus.language ||
                          'English'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className="text-xs text-gray-600 block mb-1">
                        Difficulty
                      </span>
                      <p className="text-sm font-medium text-gray-900">
                        {generationStatus.difficulty_level || 'Intermediate'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <span className="text-xs text-gray-600 block mb-1">
                        Tone
                      </span>
                      <p className="text-sm font-medium text-gray-900">
                        {generationStatus.tone || 'Formal'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Core Generation Stages */}
          <div className="space-y-4">
            {stages.map((stage, index) => renderTimelineItem(stage, index))}
          </div>

          {/* Translations produced from this course. Rendered only once the
              course document exists — before that there is nothing to translate. */}
          {generationStatus?.pedagogical_course_id && (
            <TranslationProgressSection
              courseId={generationStatus.pedagogical_course_id}
            />
          )}

          {/* Action Buttons — behaviour depends on generation completion state */}
          {generationStatus && generationStatus.pedagogical_course_id && (
            <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
              {isCourseGenerationComplete(generationStatus) ? (
                <>
                  {/* Generation complete — show both buttons */}
                  <button
                    onClick={() =>
                      navigate(
                        `/admin/edit-course/${generationStatus.pedagogical_course_id}`,
                      )
                    }
                    disabled={!canEditPublish}
                    title={
                      !canEditPublish
                        ? 'You do not have permission to edit & publish courses'
                        : undefined
                    }
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-all font-medium shadow-lg ${
                      canEditPublish
                        ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white hover:from-primary-700 hover:to-purple-700 hover:shadow-xl'
                        : 'bg-gradient-to-r from-primary-600 to-purple-600 text-white opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <Play className="w-5 h-5" />
                    Edit & Publish Course
                  </button>
                  <button
                    onClick={() =>
                      navigate(
                        `/admin/ai-learn/${generationStatus.pedagogical_course_id}`,
                      )
                    }
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  >
                    <Eye className="w-5 h-5" />
                    Preview Course
                  </button>
                </>
              ) : (
                /* Generation in progress — show single button to view the course */
                <button
                  onClick={() =>
                    navigate(
                      `/admin/edit-course/${generationStatus.pedagogical_course_id}`,
                    )
                  }
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all font-medium shadow-lg hover:shadow-xl"
                >
                  <Eye className="w-5 h-5" />
                  Check Course Progress
                </button>
              )}
            </div>
          )}
          {/* Generate Another Course — only when generation ended without a course */}
          {!isGenerating &&
            generationStatus &&
            !generationStatus.pedagogical_course_id && (
              <div className="mt-8 flex items-center justify-center">
                <button
                  onClick={() => navigate('/admin/course/add-course')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all font-medium shadow-lg hover:shadow-xl"
                >
                  <Sparkles className="w-5 h-5" />
                  Generate Another Course
                </button>
              </div>
            )}

          {/* Regenerate Full Course Button - Show when stuck or failed */}
          {shouldShowRegenerateFullCourseButton() && (
            <div className="mt-4 flex items-center justify-center">
              <button
                onClick={handleRegenerateFullCourse}
                disabled={isRegeneratingFullCourse}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-xl hover:from-red-700 hover:to-orange-700 transition-all font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegeneratingFullCourse ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Regenerate Full Course
                  </>
                )}
              </button>
            </div>
          )}

          {/* Activity Log — timeline */}
          {generationStatus?.audit_logs &&
            generationStatus.audit_logs.length > 0 && (
            <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hidden">
              {/* Hidden for future */}
                <button
                  className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white hover:from-gray-800 hover:to-gray-700 transition-colors"
                  onClick={() =>
                    setExpandedStages((prev) => {
                      const next = new Set(prev);
                      if (next.has('__audit_log')) next.delete('__audit_log');
                      else next.add('__audit_log');
                      return next;
                    })
                  }
                >
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold text-sm tracking-wide uppercase">
                      Activity Log
                    </span>
                    <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">
                      {generationStatus.audit_logs.length} events
                    </span>
                  </div>
                  {expandedStages.has('__audit_log') ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {expandedStages.has('__audit_log') && (
                  <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto font-mono text-xs">
                    {[...generationStatus.audit_logs]
                      .reverse()
                      .map((log: any) => {
                        const ts = new Date(log.timestamp);
                        const status_lower = (log.status || '').toLowerCase();
                        const statusColor =
                          status_lower === 'completed'
                            ? 'text-emerald-400 bg-emerald-950'
                            : status_lower === 'failed'
                              ? 'text-red-400 bg-red-950'
                              : status_lower === 'processing'
                                ? 'text-blue-400 bg-blue-950'
                                : 'text-gray-400 bg-gray-800';

                        const dotColor =
                          status_lower === 'completed'
                            ? 'bg-emerald-500'
                            : status_lower === 'failed'
                              ? 'bg-red-500'
                              : status_lower === 'processing'
                                ? 'bg-blue-500'
                                : 'bg-gray-500';

                        return (
                          <div
                            key={log._id}
                            className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group"
                          >
                            {/* Timestamp */}
                            <span className="text-gray-400 whitespace-nowrap flex-shrink-0 pt-0.5 tabular-nums">
                              {ts.toLocaleTimeString('en-GB', {
                                hour12: false,
                              })}
                              .{String(ts.getMilliseconds()).padStart(3, '0')}
                            </span>

                            {/* Status dot */}
                            <span
                              className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotColor}`}
                            />

                            {/* Status badge */}
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${statusColor}`}
                            >
                              {log.status}
                            </span>

                            {/* Step name */}
                            <span className="text-gray-700 font-semibold flex-shrink-0">
                              {log.step_name}
                            </span>

                            {/* Message */}
                            <span className="text-gray-500 truncate">
                              {log.message}
                            </span>

                            {/* Duration if present */}
                            {log.meta?.duration_ms && (
                              <span className="ml-auto text-amber-600 flex-shrink-0 tabular-nums">
                                {log.meta.duration_ms >= 60000
                                  ? `${(log.meta.duration_ms / 60000).toFixed(1)}m`
                                  : `${(log.meta.duration_ms / 1000).toFixed(1)}s`}
                              </span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {renderImagePreviewModal()}

      {/* Feedback Modal */}
      {renderFeedbackModal()}

      {/* Regenerate Full Course Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRegenerateConfirmModal}
        title="Regenerate Full Course"
        message="This will regenerate the ENTIRE course from scratch. All current progress will be reset and generation will restart from metadata stage. Any pending jobs will be canceled. This action cannot be undone."
        type="danger"
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
        onConfirm={confirmRegenerateFullCourse}
        onCancel={() => setShowRegenerateConfirmModal(false)}
      />

      {/* Course Generation Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          {/* Popper Confetti Effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(80)].map((_, i) => {
              const colors = [
                'bg-yellow-400',
                'bg-blue-400',
                'bg-green-400',
                'bg-red-400',
                'bg-purple-400',
                'bg-pink-400',
                'bg-orange-400',
                'bg-cyan-400',
              ];
              const shapes = ['rounded-full', 'rounded-none', ''];

              return (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    animation: `popper-${i} 3s ease-out forwards`,
                  }}
                >
                  <div
                    className={`w-3 h-3 ${colors[Math.floor(Math.random() * colors.length)]} ${shapes[Math.floor(Math.random() * shapes.length)]}`}
                    style={{
                      transform: `rotate(${Math.random() * 360}deg) scale(${0.5 + Math.random() * 0.8})`,
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Celebration Content */}
          <div className="relative bg-white rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl transform animate-pulse w-[50rem] h-[30rem]">
            {/* Close Button */}
            <button
              onClick={() => setShowCelebration(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Success Icon */}
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              🎉 Course Generated Successfully!
            </h2>
            <p className="text-gray-600 mb-4">
              Your AI-powered course is ready for editing and publishing.
            </p>

            {/* Course Details */}
            {generationStatus && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Course Details
                </h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    📚{' '}
                    {generationStatus.course_skeleton_data?.module_structure
                      ?.total_modules || 0}{' '}
                    modules created
                  </div>
                  <div>⏱️ Total time: {getOverallTimeTaken()}</div>
                  <div>🎯 Ready for editing</div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {/* <div className="space-y-3">
              <button
                onClick={() => {
                  if (generationStatus?.pedagogical_course_id) {
                    navigate(`/admin/edit-course/${generationStatus.pedagogical_course_id}`);
                  }
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Edit className="w-5 h-5 inline mr-2" />
                Edit & Publish Course
              </button>

              <button
                onClick={() => {
                  if (generationStatus?.pedagogical_course_id) {
                    navigate(`/admin/ai-learn/${generationStatus.pedagogical_course_id}`);
                  }
                }}
                className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
              >
                <Eye className="w-5 h-5 inline mr-2" />
                Preview Course
              </button>

              <button
                onClick={() => setShowCelebration(false)}
                className="w-full px-6 py-2 text-gray-500 hover:text-gray-700 transition-colors font-medium"
              >
                Stay on This Page
              </button>
            </div> */}
          </div>
        </div>
      )}

      {/* Custom CSS for popper animations */}
      <style>{`
        @keyframes popper {
          0% {
            transform: translate(-50%, -50%) scale(0.2);
            opacity: 1;
          }
          15% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 1;
          }
          100% {
            transform: translate(var(--end-x), var(--end-y)) scale(0.8) rotate(720deg);
            opacity: 0;
          }
        }

        ${[...Array(80)]
          .map((_, i) => {
            const angle = (i / 80) * 360;
            const distance = 300 + Math.random() * 400;
            const endX = Math.cos((angle * Math.PI) / 180) * distance;
            const endY = Math.sin((angle * Math.PI) / 180) * distance;

            return `
            @keyframes popper-${i} {
              0% {
                transform: translate(-50%, -50%) scale(0);
                opacity: 1;
              }
              10% {
                transform: translate(-50%, -50%) scale(1.2);
                opacity: 1;
              }
              100% {
                transform: translate(${endX}px, ${endY}px) scale(0.5) rotate(${Math.random() * 720}deg);
                opacity: 0;
              }
            }
          `;
          })
          .join('')}
      `}</style>
    </>
  );
}
